// Локальное хранилище: единственный источник данных для UI (§4.2 документа 09). Сеть в него пишет,
// UI из него читает — и никогда наоборот. Отсюда два правила, которые здесь и живут:
//   • своё сообщение появляется в базе ДО отправки (state = pending) и подменяется серверной версией;
//   • курсор стрима хранится рядом с данными, иначе после перезапуска непонятно, с чего догонять.

import type { Database, Row, SqlValue } from "./db/database.ts";
import { schema, schemaVersion } from "./db/schema.ts";

/** Состояние отправки. */
export type MessageState = "pending" | "sent" | "failed";

/** Системное событие: что произошло, кто сделал, с кем и со значением (`yofis.v1.SystemEvent`). */
/** Реакция на сообщение: эмодзи, сколько человек поставили и поставил ли я (`yofis.v1.Reaction`). */
export interface Reaction {
  emoji: string;
  count: number;
  mine: boolean;
}

export interface SystemEvent {
  /** `yofis.v1.SystemEventType`. */
  type: number;
  actor: string;
  subjects: string[];
  /** Новое название, идентификатор закреплённого сообщения, длительность звонка в секундах. */
  value: string;
}

/** Сообщение так, как его видит UI. */
export interface LocalMessage {
  id: string;
  chatId: string;
  /** Вид сообщения (`yofis.v1.MessageKind`): системные рисуются плашкой, а не пузырём. */
  kind: number;
  clientMsgId: string;
  sender: string;
  text: string;
  createdAt: number;
  editedAt: number | undefined;
  deleted: boolean;
  /** Тело не расшифровалось на узле (контракт 0.14): показывать словами, а не пустым сообщением. */
  unreadable: boolean;
  /**
   * Системное событие, если это оно: узел отдаёт структуру, фразу собирает клиент.
   *
   * Пока клиент показывал поле `text`, которого у системных событий нет, они были невидимы: человек
   * не видел ни «Иван добавлен», ни даже создания чата — в ленте оставалось пустое место.
   */
  system?: SystemEvent;
  /** Реакции на сообщение; пусто — их нет. */
  reactions?: Reaction[];
  state: MessageState;
  replyToId: string | undefined;
}

/** Чат так, как его видит список. */
export interface LocalChat {
  id: string;
  kind: number;
  title: string;
  updatedAt: number;
  lastMessageId: string | undefined;
  unread: number;
  archived: boolean;
  muted: boolean;
  pinned: boolean;
  /** Сколько участников в чате; 0 — неизвестно (личный чат или старая запись). */
  memberCount: number;
}

/** Запись очереди исходящих. */
export interface OutboxEntry {
  clientMsgId: string;
  chatId: string;
  payload: string;
  attempts: number;
  nextAttemptAt: number;
  lastError: string;
}

const stateCursor = "stream.cursor";
const stateSchema = "schema.version";

/** Хранилище поверх локальной базы. */
export class LocalStore {
  readonly #db: Database;

  private constructor(db: Database) {
    this.#db = db;
  }

  /**
   * Открывает хранилище. Если база старой версии — таблицы **пересоздаются**: клиентская база
   * одноразовая, перезалить её с сервера дешевле и честнее, чем мигрировать вслепую.
   *
   * Именно пересоздаются, а не очищаются. `CREATE TABLE IF NOT EXISTS` на существующей таблице не
   * делает ничего, поэтому очистка строк оставляла бы **старую форму**: новая колонка (`system`
   * в версии 4) не появлялась бы, и первая же запись падала бы с «no such column» — у всех, кто
   * обновился, и ни у кого, кто ставил с нуля. Тесты этого не видят по построению: у них база всегда
   * свежая.
   */
  static open(db: Database): LocalStore {
    db.run(schema);
    const current = db.get(`SELECT value FROM state WHERE key = ?`, [stateSchema]);
    const version = current === undefined ? 0 : Number(current["value"]);
    if (version !== schemaVersion) {
      db.transaction(() => {
        for (const table of ["messages", "members", "chats", "files", "outbox", "state"]) {
          db.run(`DROP TABLE IF EXISTS ${table}`);
        }
      });
      db.run(schema);
      db.run(`INSERT INTO state (key, value) VALUES (?, ?)`, [stateSchema, String(schemaVersion)]);
    }
    return new LocalStore(db);
  }

  /**
   * Пачка изменений одной транзакцией. Сто событий по одной транзакции на каждое — это сто раз пройти
   * через WASM-границу и сто раз зафиксировать журнал SQLite; одной транзакцией то же самое дешевле в
   * разы, а при ошибке ничего не остаётся наполовину применённым.
   */
  batch<T>(body: () => T): T {
    return this.#db.transaction(body);
  }

  /** Курсор стрима: с него продолжаем догон после разрыва и перезапуска. */
  get cursor(): string {
    const row = this.#db.get(`SELECT value FROM state WHERE key = ?`, [stateCursor]);
    return row === undefined ? "" : String(row["value"]);
  }

  set cursor(value: string) {
    this.#db.run(
      `INSERT INTO state (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
      [stateCursor, value],
    );
  }

  /** Полная перезагрузка: после `resync_required` содержимое пересобирается с сервера (§5.8 контракта). */
  reset(): void {
    this.#db.transaction(() => {
      for (const table of ["messages", "members", "chats", "files"]) {
        this.#db.run(`DELETE FROM ${table}`);
      }
      this.#db.run(`DELETE FROM state WHERE key = ?`, [stateCursor]);
    });
  }

  /** Список чатов: закреплённые сверху, дальше по свежести — тот же порядок, что у сервера. */
  /**
   * Состав чата целиком — как его отдал узел.
   *
   * Отметки прочтения приходят тем же списком (`Member.last_read_message_id`), и это единственный
   * способ узнать их тому, кто вошёл позже: событие о прочтении он не застал. Отметка, которая уже
   * есть в базе, не понижается: у события может быть более свежее число, чем у ответа.
   */
  upsertMembers(chatId: string, members: { address: string; role: number; lastReadMessageId: string }[]): void {
    // Сравнение отметок — числом, а не строкой: снежинки разной длины строкой сравниваются неверно
    // («10» меньше «9»), а разной длины они станут ровно тогда, когда никто уже не будет помнить,
    // почему тут была строка.
    const larger = (a: string, b: string): string => {
      if (a === "") {
        return b;
      }
      if (b === "") {
        return a;
      }
      try {
        return BigInt(a) >= BigInt(b) ? a : b;
      } catch {
        return a;
      }
    };
    this.batch(() => {
      for (const member of members) {
        const known = this.#db.get(
          `SELECT last_read_message_id FROM members WHERE chat_id = ? AND address = ?`,
          [chatId, member.address],
        );
        const read = larger(String(known?.["last_read_message_id"] ?? ""), member.lastReadMessageId);
        this.#db.run(
          `INSERT INTO members (chat_id, address, role, last_read_message_id) VALUES (?, ?, ?, ?)
           ON CONFLICT(chat_id, address) DO UPDATE SET role = excluded.role,
             last_read_message_id = excluded.last_read_message_id`,
          [chatId, member.address, member.role, read],
        );
      }
    });
  }

  /** Реакции пришли отдельным событием: сообщение уже лежит в базе, меняется только их строка. */
  setReactions(messageId: string, reactions: Reaction[]): void {
    this.#db.run(`UPDATE messages SET reactions = ? WHERE id = ?`, [
      reactions.length === 0 ? "" : JSON.stringify(reactions),
      messageId,
    ]);
  }

  /** Кто в чате: адреса участников. Пусто — состав ещё не загружали. */
  members(chatId: string): string[] {
    return this.#db
      .all(`SELECT address FROM members WHERE chat_id = ? ORDER BY address`, [chatId])
      .map((row) => String(row["address"] ?? ""));
  }

  /**
   * Состав чата изменился на одного человека.
   *
   * Число участников приходит в описании чата, но узел присылает описание не на каждое изменение:
   * при добавлении — присылает, при удалении — нет. Поэтому счёт ведётся и по событию о составе;
   * пришедшее описание всё равно перезапишет его точным числом.
   */
  changeMembers(chatId: string, by: number): void {
    this.#db.run(`UPDATE chats SET member_count = MAX(0, member_count + ?) WHERE id = ?`, [by, chatId]);
  }

  /** Участник больше не в чате: его отметка прочтения и роль больше не наши. */
  removeMember(chatId: string, address: string): void {
    this.#db.run(`DELETE FROM members WHERE chat_id = ? AND address = ?`, [chatId, address]);
  }

  /**
   * Чужая отметка прочтения. Строка участника заводится по ней же: списка участников клиент пока
   * не загружает, а знать, кто до чего дочитал, нужно уже сейчас.
   */
  setRead(chatId: string, address: string, messageId: string): void {
    this.#db.run(
      `INSERT INTO members (chat_id, address, last_read_message_id) VALUES (?, ?, ?)
       ON CONFLICT(chat_id, address) DO UPDATE SET last_read_message_id = excluded.last_read_message_id`,
      [chatId, address, messageId],
    );
  }

  /**
   * До какого сообщения дочитали ВСЕ, кроме указанного адреса (обычно — кроме себя).
   *
   * Берётся минимум: вторая галочка означает «прочитали все», и один отставший её снимает. Пусто —
   * не знаем ни про кого: в этом случае галочка остаётся одинарной, а не «прочитано».
   */
  readUpTo(chatId: string, except: string): string {
    const chat = this.chat(chatId);
    if (chat === undefined) {
      return "";
    }
    // Сколько человек, кроме меня, должны были прочитать. У личного чата это всегда один, у группы —
    // состав минус я. Без этого числа «прочитали все» превращается в «прочитал хоть кто-то», а это
    // разные утверждения: вторая галочка обещает первое.
    const others = chat.kind === 1 ? 1 : chat.memberCount - 1;
    const rows = this.#db
      .all(`SELECT address, last_read_message_id FROM members WHERE chat_id = ? AND address <> ?`, [chatId, except]);
    if (others <= 0 || rows.length < others) {
      return "";
    }
    let min: bigint | undefined;
    for (const row of rows) {
      const raw = String(row["last_read_message_id"] ?? "");
      if (raw === "") {
        return "";
      }
      const value = BigInt(raw);
      if (min === undefined || value < min) {
        min = value;
      }
    }
    return min === undefined ? "" : String(min);
  }

  /**
   * Счётчик непрочитанного растёт у клиента, а не только на сервере.
   *
   * Точное число знает узел, но присылает он его только в описании чата — то есть при синхронизации
   * и перезагрузке. Живое сообщение приходит отдельным событием без описания чата, и пока клиент не
   * считал сам, значок непрочитанного **не появлялся вовсе, пока страницу не перезагрузят**: в
   * десктопе окно живёт днями, то есть не появлялся никогда. Сервер остаётся источником истины —
   * при следующей синхронизации его число перезаписывает это.
   */
  bumpUnread(chatId: string): void {
    this.#db.run(`UPDATE chats SET unread = unread + 1 WHERE id = ?`, [chatId]);
  }

  /** Прочитано: значок гаснет сразу, не дожидаясь ответа узла. */
  clearUnread(chatId: string): void {
    this.#db.run(`UPDATE chats SET unread = 0 WHERE id = ?`, [chatId]);
  }

  chats(options: { includeArchived?: boolean } = {}): LocalChat[] {
    const where = options.includeArchived === true ? "" : "WHERE archived = 0";
    return this.#db
      .all(`SELECT * FROM chats ${where} ORDER BY pinned DESC, updated_at DESC, id DESC`)
      .map(toChat);
  }

  chat(id: string): LocalChat | undefined {
    const row = this.#db.get(`SELECT * FROM chats WHERE id = ?`, [id]);
    return row === undefined ? undefined : toChat(row);
  }

  upsertChat(chat: LocalChat): void {
    this.#db.run(
      `INSERT INTO chats (id, kind, title, updated_at, last_message_id, unread, archived, muted, pinned, member_count)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(id) DO UPDATE SET
         kind = excluded.kind, title = excluded.title, updated_at = excluded.updated_at,
         last_message_id = excluded.last_message_id, unread = excluded.unread,
         archived = excluded.archived, muted = excluded.muted, pinned = excluded.pinned,
         member_count = excluded.member_count`,
      [
        chat.id,
        chat.kind,
        chat.title,
        chat.updatedAt,
        chat.lastMessageId ?? null,
        chat.unread,
        chat.archived ? 1 : 0,
        chat.muted ? 1 : 0,
        chat.pinned ? 1 : 0,
        chat.memberCount,
      ],
    );
  }

  /**
   * История чата от новых к старым, страницами: UI не тянет всю историю (§4.2).
   *
   * Якорь — `beforeId`, а не время: сообщения, отправленные в одну секунду, по времени неразличимы
   * (в базе оно с точностью до секунды), и страницы по времени то теряли бы строки, то повторяли их.
   * Сравнение идёт по паре (время, id) — ровно в том порядке, в каком лента и рисуется.
   */
  messages(chatId: string, options: { beforeId?: string; limit?: number } = {}): LocalMessage[] {
    const limit = options.limit ?? 50;
    const params: SqlValue[] = [chatId];
    let where = `chat_id = ?`;
    if (options.beforeId !== undefined) {
      where += ` AND (created_at, id) < (SELECT created_at, id FROM messages WHERE id = ?)`;
      params.push(options.beforeId);
    }
    params.push(limit);
    return this.#db
      .all(`SELECT * FROM messages WHERE ${where} ORDER BY created_at DESC, id DESC LIMIT ?`, params)
      .map(toMessage);
  }

  message(id: string): LocalMessage | undefined {
    const row = this.#db.get(`SELECT * FROM messages WHERE id = ?`, [id]);
    return row === undefined ? undefined : toMessage(row);
  }

  /** Сообщение из сети или своё черновое; повторный вызов с тем же id перезаписывает. */
  upsertMessage(message: LocalMessage): void {
    this.#db.run(
      `INSERT INTO messages (id, chat_id, client_msg_id, sender, kind, text, created_at, edited_at, deleted, state, reply_to_id, unreadable, system, reactions)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(id) DO UPDATE SET
         chat_id = excluded.chat_id, client_msg_id = excluded.client_msg_id, sender = excluded.sender,
         kind = excluded.kind, text = excluded.text, created_at = excluded.created_at,
         edited_at = excluded.edited_at, deleted = excluded.deleted, state = excluded.state,
         unreadable = excluded.unreadable, system = excluded.system, reactions = excluded.reactions,
         reply_to_id = excluded.reply_to_id`,
      [
        message.id,
        message.chatId,
        message.clientMsgId,
        message.sender,
        message.kind,
        message.text,
        message.createdAt,
        message.editedAt ?? null,
        message.deleted ? 1 : 0,
        message.state,
        message.replyToId ?? null,
        message.unreadable ? 1 : 0,
        message.system === undefined ? "" : JSON.stringify(message.system),
        message.reactions === undefined || message.reactions.length === 0 ? "" : JSON.stringify(message.reactions),
      ],
    );
  }

  /**
   * Подменяет черновик серверной версией: строка с временным идентификатором исчезает, появляется
   * настоящая. Делается одной транзакцией — иначе UI успеет показать сообщение дважды.
   */
  replacePending(clientMsgId: string, server: LocalMessage): void {
    this.#db.transaction(() => {
      // Убираем локальную строку независимо от состояния: после неудачи она помечена `failed`, и если
      // удалять только `pending`, повторная отправка оставит в списке призрак рядом с настоящим.
      this.#db.run(`DELETE FROM messages WHERE chat_id = ? AND client_msg_id = ? AND id <> ?`, [
        server.chatId,
        clientMsgId,
        server.id,
      ]);
      this.upsertMessage(server);
      this.#db.run(`DELETE FROM outbox WHERE client_msg_id = ?`, [clientMsgId]);
    });
  }

  /** Пометить черновик неотправленным: UI показывает это состояние и предлагает повтор. */
  markFailed(clientMsgId: string, error: string, nextAttemptAt: number): void {
    this.#db.transaction(() => {
      this.#db.run(`UPDATE messages SET state = 'failed' WHERE client_msg_id = ? AND state = 'pending'`, [
        clientMsgId,
      ]);
      this.#db.run(
        `UPDATE outbox SET attempts = attempts + 1, last_error = ?, next_attempt_at = ? WHERE client_msg_id = ?`,
        [error, nextAttemptAt, clientMsgId],
      );
    });
  }

  /** Поставить в очередь исходящих вместе с черновиком сообщения. */
  enqueue(entry: OutboxEntry, draft: LocalMessage): void {
    this.#db.transaction(() => {
      this.upsertMessage(draft);
      this.#db.run(
        `INSERT INTO outbox (client_msg_id, chat_id, payload, attempts, next_attempt_at, last_error)
         VALUES (?, ?, ?, ?, ?, '')
         ON CONFLICT(client_msg_id) DO UPDATE SET payload = excluded.payload`,
        [entry.clientMsgId, entry.chatId, entry.payload, entry.attempts, entry.nextAttemptAt],
      );
    });
  }

  /** Что пора отправлять: срок повтора наступил. */
  due(now: number): OutboxEntry[] {
    return this.#db
      .all(`SELECT * FROM outbox WHERE next_attempt_at <= ? ORDER BY next_attempt_at, client_msg_id`, [now])
      .map((row) => ({
        clientMsgId: String(row["client_msg_id"]),
        chatId: String(row["chat_id"]),
        payload: String(row["payload"]),
        attempts: Number(row["attempts"]),
        nextAttemptAt: Number(row["next_attempt_at"]),
        lastError: String(row["last_error"]),
      }));
  }

  /** Сколько сообщений ждёт отправки — UI показывает это состояние честно, а не прячет. */
  pendingCount(): number {
    const row = this.#db.get(`SELECT COUNT(*) AS n FROM outbox`);
    return row === undefined ? 0 : Number(row["n"]);
  }

  /**
   * Локальный поиск по истории, которая уже лежит в базе. Работает всегда — и когда узел серверный
   * поиск выключил, и когда сети нет вовсе; это основной путь поиска (§4.2 документа 09).
   */
  search(query: string, limit = 50): LocalMessage[] {
    const needle = `%${query.replaceAll("%", "\\%").replaceAll("_", "\\_")}%`;
    return this.#db
      .all(
        `SELECT * FROM messages WHERE deleted = 0 AND text LIKE ? ESCAPE '\\'
         ORDER BY created_at DESC, id DESC LIMIT ?`,
        [needle, limit],
      )
      .map(toMessage);
  }

  /** Байты базы — для сохранения платформой (OPFS в браузере, файл на десктопе). */
  export(): Uint8Array {
    return this.#db.export();
  }
}

function toChat(row: Row): LocalChat {
  return {
    id: String(row["id"]),
    kind: Number(row["kind"]),
    title: String(row["title"]),
    updatedAt: Number(row["updated_at"]),
    lastMessageId: row["last_message_id"] === null ? undefined : String(row["last_message_id"]),
    unread: Number(row["unread"]),
    archived: Number(row["archived"]) === 1,
    muted: Number(row["muted"]) === 1,
    pinned: Number(row["pinned"]) === 1,
    memberCount: Number(row["member_count"] ?? 0),
  };
}

function toMessage(row: Row): LocalMessage {
  return {
    id: String(row["id"]),
    chatId: String(row["chat_id"]),
    clientMsgId: String(row["client_msg_id"]),
    sender: String(row["sender"]),
    kind: Number(row["kind"]),
    text: String(row["text"]),
    createdAt: Number(row["created_at"]),
    editedAt: row["edited_at"] === null ? undefined : Number(row["edited_at"]),
    deleted: Number(row["deleted"]) === 1,
    unreadable: Number(row["unreadable"] ?? 0) === 1,
    ...parseSystem(String(row["system"] ?? "")),
    ...parseReactions(String(row["reactions"] ?? "")),
    state: String(row["state"]) as MessageState,
    replyToId: row["reply_to_id"] === null ? undefined : String(row["reply_to_id"]),
  };
}

/** Системное событие из строки базы; пустая строка — обычное сообщение. */
/** Реакции из строки; мусор в базе не должен ронять ленту — в худшем случае реакций не будет видно. */
function parseReactions(raw: string): { reactions?: Reaction[] } {
  if (raw === "") {
    return {};
  }
  try {
    return { reactions: JSON.parse(raw) as Reaction[] };
  } catch {
    return {};
  }
}

function parseSystem(raw: string): { system?: SystemEvent } {
  if (raw === "") {
    return {};
  }
  try {
    return { system: JSON.parse(raw) as SystemEvent };
  } catch {
    // Испорченная запись не должна ронять чтение всей страницы истории: сообщение просто
    // останется без системной подписи.
    return {};
  }
}
